// /thai-visa/$destination/$type — Wave 3 Batch 3 A5 leaf (Thai)
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import {
  OUTBOUND_DESTINATIONS, OUTBOUND_VISA_TYPES,
  getOutboundDestination, getOutboundVisaType,
} from "@/data/thai-outbound-visas";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { Plane, MessageCircle, Phone, BadgeCheck, MapPin, Clock, Wallet, ShieldCheck } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/thai-visa/$destination/$type")({
  loader: ({ params }) => {
    const d = getOutboundDestination(params.destination);
    const v = getOutboundVisaType(params.type);
    if (!d || !v) throw notFound();
    return { destination: params.destination, type: params.type };
  },
  head: ({ params }) => {
    const d = getOutboundDestination(params.destination);
    const v = getOutboundVisaType(params.type);
    if (!d || !v) return { meta: [{ title: ctaTitle("Not found", "/thai-visa/$destination/$type", "th")}] };
    const url = `${SITE}/thai-visa/${d.slug}/${v.slug}`;
    const price = (d.visaFeeThb + v.bundleThb).toLocaleString();
    const title = `${v.nameTh} ${d.nameTh} จากไทย · แปลรับรอง + ${d.hague ? "Apostille" : `${d.embassyTh}`} | NYC`;
    const desc = `บริการเตรียมเอกสาร ${v.nameTh} ${d.nameTh} สำหรับพลเมืองไทย — แปลรับรอง MFA + ${d.hague ? "Apostille (Hague)" : `รับรอง ${d.embassyTh}`} + ตรวจสอบ VFS/${d.vfsPartner || "สถานทูต"}. เริ่มต้น THB ${price}, ${v.turnaround}. 99.6% first-pass acceptance.`;
    return {
      meta: [
        { title: ctaTitle(title, "/thai-visa/$destination/$type", "th") }, { name: "description", content: ctaDesc(desc, "/thai-visa/$destination/$type", "th")},
        { name: "keywords", content: `ขอวีซ่า${d.nameTh}, ${v.nameTh} ${d.nameTh}, แปลเอกสารวีซ่า${d.nameTh}, Apostille ${d.nameEn}, ${d.embassyEn}` },
        { property: "og:title", content: ctaTitle(title, "/thai-visa/$destination/$type", "th")}, { property: "og:description", content: ctaDesc(desc, "/thai-visa/$destination/$type", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/thai-visa/${d.slug}/${v.slug}`, `/en/thai-visa/${d.slug}/${v.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema(),
          { type: "application/ld+json" as const, children: JSON.stringify({
            "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
            name: `${v.nameEn} document preparation for ${d.nameEn}`,
            serviceType: `Thai outbound ${v.labelEn.toLowerCase()} visa document service`,
            areaServed: [{ "@type": "Country", name: "Thailand" }, { "@type": "Country", name: d.nameEn }],
            provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Language Institute", telephone: "+66832494999", url: SITE },
            offers: { "@type": "Offer", price: d.visaFeeThb + v.bundleThb, priceCurrency: "THB", availability: "https://schema.org/InStock" },
            aggregateRating: aggregateRatingNode(url),
          }) },
          { type: "application/ld+json" as const, children: JSON.stringify({
            "@context": "https://schema.org", "@type": "FAQPage", "@id": `${url}#faq`,
            mainEntity: [
              { q: `บริการนี้ครอบคลุมอะไรบ้าง?`, a: `${v.taglineTh} ปลายทาง ${d.nameTh} — ${d.hague ? "รับรอง Apostille (Hague)" : `รับรอง ${d.embassyTh}`} ครบชุด` },
              { q: `${d.nameTh} อยู่ใน Hague Apostille ไหม?`, a: d.hague ? `ใช่ — Apostille ที่ MFA ไทย ใช้ได้โดยตรงไม่ต้องผ่านสถานทูต` : `ไม่ใช่ — ต้อง MFA + รับรองที่ ${d.embassyTh} เพิ่มอีกขั้น` },
              { q: `ใช้เวลาเตรียมเอกสารกี่วัน?`, a: `${v.turnaround} (สถานทูตพิจารณาต่ออีก ${d.processingDays})` },
              { q: `ค่าบริการรวมทั้งหมด?`, a: `เริ่มต้น THB ${price} รวมค่าธรรมเนียมสถานทูต THB ${d.visaFeeThb.toLocaleString()} + ชุดเอกสาร THB ${v.bundleThb.toLocaleString()}` },
              { q: `นัดหมาย VFS/${d.vfsPartner || "สถานทูต"} จำเป็นไหม?`, a: d.bioRequired ? `จำเป็น — ${d.vfsPartner} ต้องจอง biometrics ล่วงหน้า` : `ไม่จำเป็น — ยื่นตรงที่ ${d.embassyTh}` },
            ].map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
          }) },
          { type: "application/ld+json" as const, children: JSON.stringify({
            "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
              { "@type": "ListItem", position: 2, name: "วีซ่าไปต่างประเทศ", item: `${SITE}/thai-visa` },
              { "@type": "ListItem", position: 3, name: d.nameTh, item: `${SITE}/thai-visa/${d.slug}` },
              { "@type": "ListItem", position: 4, name: v.nameTh, item: url },
            ],
          }) },
        ]),
      ],
    };
  },
  component: ThaiOutboundLeaf,
});

function ThaiOutboundLeaf() {
  const { destination, type } = Route.useLoaderData() as { destination: string; type: string };
  const d = getOutboundDestination(destination)!;
  const v = getOutboundVisaType(type)!;
  const price = (d.visaFeeThb + v.bundleThb).toLocaleString();
  const related = OUTBOUND_VISA_TYPES.filter((x) => x.slug !== v.slug);
  const otherDest = OUTBOUND_DESTINATIONS.filter((x) => x.slug !== d.slug).slice(0, 9);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link><span className="px-1.5 text-white/30">/</span>
            <Link to="/thai-visa" className="hover:text-gold">วีซ่าไปต่างประเทศ</Link><span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{d.nameTh} · {v.labelTh}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Plane className="h-3.5 w-3.5" /> {d.regionTh} · {v.labelTh}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            {v.nameTh}สำหรับพลเมืองไทย ปลายทาง <span className="text-gold">{d.nameTh}</span>
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            {v.taglineTh} · {d.hague ? "Apostille (Hague Member)" : `MFA + ${d.embassyTh}`} · เริ่มต้น THB {price} · {v.turnaround}
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md"><MessageCircle className="h-4 w-4" /> LINE @nycli</a>
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md"><Phone className="h-4 w-4" /> 083 249 4999</a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800"><BadgeCheck className="h-3 w-3" /> Quotable</span>
          {v.nameTh}ปลายทาง{d.nameTh}สำหรับพลเมืองไทย: เตรียมชุดเอกสารครบพร้อมแปลรับรอง + {d.hague ? "Apostille (Hague)" : `รับรอง ${d.embassyTh}`}, เริ่มต้น THB {price}, {v.turnaround}. ค่าธรรมเนียมสถานทูต THB {d.visaFeeThb.toLocaleString()}. 99.6% first-pass acceptance.
        </blockquote>

        {/* Snapshot grid */}
        <section className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: MapPin, label: "สถานทูต", value: d.embassyTh, sub: d.embassyAddr },
            { icon: Clock, label: "ระยะเวลาสถานทูต", value: d.processingDays, sub: d.embassyHours },
            { icon: Wallet, label: "ค่าใช้จ่ายรวม", value: `THB ${price}`, sub: `ค่าธรรมเนียม ${d.visaFeeThb.toLocaleString()} + เอกสาร ${v.bundleThb.toLocaleString()}` },
            { icon: ShieldCheck, label: "การรับรอง", value: d.hague ? "Apostille (Hague)" : "MFA + สถานทูต", sub: d.vfsPartner ? `ยื่นผ่าน ${d.vfsPartner}` : "ยื่นตรงสถานทูต" },
          ].map((s, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-4">
              <s.icon className="h-5 w-5 text-amber-600" />
              <div className="mt-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">{s.label}</div>
              <div className="mt-1 text-sm font-bold text-slate-900">{s.value}</div>
              <div className="mt-1 text-[11px] leading-relaxed text-slate-600">{s.sub}</div>
            </div>
          ))}
        </section>

        {/* Country hallmark */}
        <section className="mt-10 rounded-2xl border border-amber-200 bg-amber-50/40 p-5">
          <h2 className="font-display text-lg font-extrabold text-navy-deep sm:text-xl">ทำไม{d.nameTh}?</h2>
          <p className="mt-2 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">{d.hallmarkTh}</p>
        </section>

        {/* Docs checklist — universal + country quirk */}
        <section className="mt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">เอกสารที่ต้องเตรียม</h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {v.coreDocsTh.map((c, i) => (
              <li key={i} className="flex items-start gap-2 rounded-xl border border-slate-200 bg-white p-3 text-[14px] text-slate-800">
                <BadgeCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 rounded-2xl border-l-4 border-amber-500 bg-amber-50/50 p-4 text-[13px] leading-relaxed text-slate-800">
            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">ข้อควรระวังเฉพาะ {d.nameTh}</div>
            <p className="mt-1">{d.quirkTh}</p>
          </div>
        </section>

        {/* Translation policy */}
        <section className="mt-10 rounded-2xl bg-slate-50 p-5">
          <h2 className="font-display text-lg font-extrabold text-navy-deep sm:text-xl">การรับรองและแปลเอกสาร</h2>
          <p className="mt-2 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">{v.translationTh}</p>
        </section>

        <section className="mt-12"><ContactCTA /></section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย</h2>
          <div className="mt-5">
            <FaqAccordion items={[
              { q: `${d.nameTh}อยู่ใน Hague Apostille ไหม?`, a: d.hague ? `ใช่ — Apostille ที่ MFA ไทย ใช้ได้โดยตรง` : `ไม่ใช่ — ต้อง MFA + ${d.embassyTh}` },
              { q: `ต้องการ NAATI ไหม?`, a: d.slug === "australia" ? "ใช่ — ออสเตรเลียรับเฉพาะ NAATI-certified translation" : d.slug === "new-zealand" ? "รับได้ทั้ง NAATI และ MFA-legalised" : "ไม่จำเป็น — MFA legalisation ก็ใช้ได้" },
              { q: `ใช้เวลาทั้งหมดกี่วัน?`, a: `เตรียมเอกสาร ${v.turnaround} + สถานทูตพิจารณา ${d.processingDays}` },
              { q: `ค่าใช้จ่ายรวมทั้งหมด?`, a: `เริ่มต้น THB ${price} รวมค่าธรรมเนียม ${d.embassyTh}` },
              { q: `จอง VFS/${d.vfsPartner || "สถานทูต"} เอง หรือใครจอง?`, a: `NYC จองให้ผ่านระบบ ${d.vfsPartner || "สถานทูต"} โดยแนบเอกสารพร้อมสำหรับวันสัมภาษณ์` },
            ]} />
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">วีซ่าประเภทอื่นสำหรับ {d.nameTh}</h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to="/thai-visa/$destination/$type" params={{ destination: d.slug, type: r.slug }} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm hover:border-gold/50">
                <span className="font-semibold text-slate-800">{r.nameTh}</span>
                <span className="text-[11px] text-slate-500">THB {(d.visaFeeThb + r.bundleThb).toLocaleString()}</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ปลายทางยอดนิยมอื่น ๆ</h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {otherDest.map((o) => (
              <Link key={o.slug} to="/thai-visa/$destination/$type" params={{ destination: o.slug, type: v.slug }} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm hover:border-gold/50">
                <span className="font-semibold text-slate-800">{v.labelTh} · {o.nameTh}</span>
                <span className="text-[11px] text-slate-500">{o.hague ? "Apostille" : "Embassy"}</span>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}
